import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, CardHeader, CardTitle, StatusChip } from '@monigo/ui';
import { LanguageToggle } from '../../components/LanguageToggle.js';
import { useSession } from '../../lib/store/session.js';
import { useKycStatus } from '../kyc/api.js';
import { useSettings } from './api.js';

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { user, signOut } = useSession();
  const navigate = useNavigate();
  const kycStatus = useKycStatus();
  const supportSettings = useSettings();

  const profile = kycStatus.data; // has_completed_kyc from here
  const submission = kycStatus.data?.latest_submission;

  const supportEmail =
    (supportSettings.data?.find((s) => s.key === 'support.email')?.value_jsonb as { email?: string })
      ?.email ?? '';

  const kycLabel = profile?.has_completed_kyc
    ? t('kyc.statusApproved')
    : submission?.status === 'pending'
      ? t('kyc.statusPending')
      : submission?.status === 'rejected'
        ? t('kyc.statusRejected')
        : t('kyc.statusNotSubmitted');

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.profileTitle')}</CardTitle>
        </CardHeader>
        <div className="flex flex-col gap-4 px-5 pb-5 text-sm">
          {user ? (
            <>
              <Row label={t('settings.email')}>
                <span className="font-medium text-text">{user.email}</span>
              </Row>
              <Row label={t('settings.phone')}>
                <span className="text-text-muted">
                  {user.phone ?? '—'}
                </span>
              </Row>
              <Row label={t('settings.memberSince')}>
                <span className="text-text-muted">
                  {new Date(user.created_at).toLocaleDateString(
                    i18n.language === 'fr' ? 'fr-FR' : 'en-US',
                    { year: 'numeric', month: 'long', day: 'numeric' },
                  )}
                </span>
              </Row>
              <Row label={t('settings.kycStatus')}>
                <StatusChip
                  status={
                    profile?.has_completed_kyc
                      ? 'COMPLETED'
                      : submission?.status === 'pending'
                        ? 'PENDING_KYC'
                        : submission?.status === 'rejected'
                          ? 'FAILED'
                          : 'DRAFT'
                  }
                />
              </Row>
            </>
          ) : null}
          <Row label={t('settings.language')}>
            <LanguageToggle />
          </Row>
        </div>
      </Card>

      {supportEmail ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('settings.contactTitle')}</CardTitle>
          </CardHeader>
          <div className="flex flex-col gap-4 px-5 pb-5 text-sm">
            <Row label={t('settings.supportEmail')}>
              <a
                href={`mailto:${supportEmail}`}
                className="font-medium text-primary hover:underline"
              >
                {supportEmail}
              </a>
            </Row>
            {supportSettings.data
              ?.find((s) => s.key === 'support.faq_url')
              ?.value_jsonb?.url ? (
              <Row label={t('settings.faq')}>
                <a
                  href={String(supportSettings.data.find((s) => s.key === 'support.faq_url')?.value_jsonb?.url ?? '')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-primary hover:underline"
                >
                  {t('settings.visitFaq')}
                </a>
              </Row>
            ) : null}
            <p className="mt-2 text-xs text-text-muted">
              {t('settings.contactDispute')}
            </p>
          </div>
        </Card>
      ) : null}

      {submission?.status === 'rejected' ? (
        <Card className="border-danger/20">
          <CardHeader>
            <CardTitle>{t('kyc.rejectedTitle')}</CardTitle>
          </CardHeader>
          <div className="flex flex-col gap-3 px-5 pb-5 text-sm">
            <p className="text-danger">
              {t('kyc.rejectedBody', { reason: submission.rejection_reason ?? t('kyc.noReason') })}
            </p>
            <Link to="/kyc">
              <Button variant="secondary">{t('kyc.retryCta')}</Button>
            </Link>
          </div>
        </Card>
      ) : null}

      <Card>
        <Button
          variant="secondary"
          onClick={async () => {
            await signOut();
            navigate('/login');
          }}
          className="w-full"
        >
          {t('settings.logout')}
        </Button>
      </Card>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="shrink-0 text-text-muted">{label}</span>
      {children}
    </div>
  );
}
